import React, { memo, useMemo } from "react";
import Spinner from "../../../../../components/bootstrap/Spinner";
import Money from "../../../../../helpers/utils/money/money";
import useCmvProducts from "../../../../../hooks/reports/useCmvProducts";
import useDarkMode from "../../../../../hooks/useDarkMode";
import useCmvHistory from "../../../../../hooks/reports/useCmvHistory";
import TableCustom from "../../../../../components/MyCustom/TableCustom";
import GraficoCmvDiario from "../../../../../components/GraficoCmvDiario";
import RankingPorCusto from "../../../../../components/RankingPorCusto";

const TabGrafico = ({ hidden, dataFilter }) => {
    const { darkModeStatus } = useDarkMode();
    const { rows, loading } = useCmvProducts(dataFilter);
    const { reports } = useCmvHistory(dataFilter);

    const totals = useMemo(() => {
        const totalQtd = rows.reduce((sum, r) => sum + (r.totalQtd || 0), 0);
        const totalCost = rows.reduce((sum, r) => sum + (r.totalCost || 0), 0);
        const totalGeneral = rows.reduce((sum, r) => sum + (r.total || 0), 0);

        let totalCustoManual = 0;
        let totalCustoPerda = 0;
        let totalCustoPromocional = 0;

        reports.forEach((r) => {
            if (r.days) {
                for (const dayKey in r.days) {
                    const day = r.days[dayKey];
                    const price = day?.priceUn ?? 0;
                    const qtd = day?.qtdExit ?? 0;
                    const priceUn = day?.priceUn ?? 0;
                    const qtdLoss = day?.qtdLoss ?? 0;
                    const qtdPromotial = day?.qtdPromotial ?? 0;

                    totalCustoManual += price * qtd;
                    totalCustoPerda += priceUn * qtdLoss;
                    totalCustoPromocional += priceUn * qtdPromotial
                }
            }
        });

        const totalSaidaReal = totalCustoManual + totalCustoPerda + totalCustoPromocional;
        const percentualCmvReal = totalGeneral > 0 ? (totalSaidaReal / totalGeneral) * 100 : 0;
        const percentualCmvAutomatico = totalGeneral > 0 ? (totalCost / totalGeneral) * 100 : 0;

        return {
            totalQtd,
            totalGeneral,
            totalCost,
            totalCustoManual,
            totalCustoPerda,
            totalCustoPromocional,
            totalSaidaReal,
            percentualCmvReal,
            percentualCmvAutomatico,
        };
    }, [rows, reports]);

    const totalRow = useMemo(
        () => [
            {
                totalQtd: totals.totalQtd,
                totalGeneral: totals.totalGeneral,
                cmv: totals.totalCost,
                totalCustoManual: totals.totalCustoManual,
                totalCustoPerda: totals.totalCustoPerda,
                totalCustoPromocional: totals.totalCustoPromocional,
                saidaReal: totals.totalSaidaReal,
                percentualCmv: totals.percentualCmvReal,
                percentualCmvAutomatico: totals.percentualCmvAutomatico,
            },
        ],
        [totals]
    );

    const columns = useMemo(
        () => [
            { 
                label: "Total de Produtos Vendidos", 
                field: "totalQtd" 
            },
            { 
                label: "Valor Total de Vendas", 
                field: "totalGeneral", 
                format: row => Money.centsToMaskMoney(row.totalGeneral),
                isCurrency: true
            },
            { 
                label: "Total saída manual", 
                field: "totalCustoManual", 
                format: row => Money.centsToMaskMoney(row.totalCustoManual),
                isCurrency: true
            },
            { 
                label: "Total Perdas", 
                field: "totalCustoPerda", 
                format: row => Money.centsToMaskMoney(row.totalCustoPerda),
                isCurrency: true
            },
            { 
                label: "Total Cortesia", field: 
                "totalCustoPromocional", 
                format: row => Money.centsToMaskMoney(row.totalCustoPromocional) ,
                isCurrency: true
            },
            {
                label: "CMV Automático total %",
                field: "{row}",
                format: (row) => {
                    const percentual = `${Number(row.percentualCmvAutomatico).toFixed(2)}%`.replace('.', ',');
                    const valor = Money.centsToMaskMoney(row.cmv);
                    return `${percentual} (${valor})`;
                },
                formatExport: row => row.percentualCmvAutomatico / 100,
                isPercentage: true
            },
            {
                label: "CMV Real Total %",
                field: "{row}",
                format: (row) => {
                    const percentual = `${Number(row.percentualCmv).toFixed(2)}%`.replace('.', ',');
                    const valor = Money.centsToMaskMoney(row.saidaReal);
                    return `${percentual} (${valor})`;
                },
                formatExport: row => row.percentualCmv / 100,
                isPercentage: true
            },
        ],
        [darkModeStatus]
    );

    return (
        <div hidden={hidden}>
            {loading ? (
                <div className="d-flex flex-column justify-content-center align-items-center h-100 w-100">
                    <Spinner size={72} color="blue" tag="span" />
                </div>
            ) : (
                <div className="row h-100 w-100 d-flex">
                    <div className="col-lg-8 mb-4 d-flex">
                        <div className="card shadow-sm w-100 h-100">
                            <div className="card-body d-flex flex-column">
                                <TableCustom
                                    icon="TableChart"
                                    fontSize={8}
                                    rows={totalRow}
                                    columns={columns}
                                    fileName="Geral-CMV"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-4 mb-4 d-flex">
                        <div className="card shadow-sm w-100 h-100">
                            <RankingPorCusto dataFilter={dataFilter} />
                        </div>
                    </div>

                    <div className="col-12">
                        <div className="card shadow-sm w-100 h-100">
                            <GraficoCmvDiario data={rows} reports={reports} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default memo(TabGrafico);